import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { Lock, Crown, Sparkles, Zap, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const CREATOR_LINK = 'https://buy.stripe.com/eVq7sN3W51WX5hj0Xq8AE01';
const STUDIO_LINK = 'https://buy.stripe.com/9B600l2S1dFF9xz49C8AE02';

export default function AIGate({ children }) {
  const { user } = useAuth();
  const { hasAIAccess, isOwner, loading, planName, plan, subscriptionStatus, permissions } = usePermissions();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }

  if (isOwner || hasAIAccess) {
    return children;
  }

  const isExpired = subscriptionStatus === 'expired';
  const isTrial = subscriptionStatus === 'trial';
  const isMarketplace = plan === 'marketplace';

  let headline = 'Subscription required to use AI tools';
  let description = 'Upgrade to a Creator AI or Studio plan to unlock AI-powered product creation, image generation, and more.';

  if (isExpired) {
    headline = 'Your subscription has expired';
    description = 'Renew your Creator AI or Studio subscription to regain access to AI tools.';
  } else if (isTrial) {
    headline = 'AI tools require a paid plan';
    description = 'Your free trial includes community and marketplace access. Upgrade to unlock AI generation.';
  } else if (isMarketplace) {
    headline = 'Your Marketplace plan does not include AI tools';
    description = 'The Marketplace plan (€29/mo) covers community and selling. Upgrade to Creator AI (€49/mo) to unlock AI generation.';
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="ai-gate-locked">
      <div className="max-w-lg w-full text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-stone-100 border border-stone-200 mb-6">
          <Lock size={28} className="text-stone-400" />
        </div>

        <h1 className="font-serif text-2xl sm:text-3xl text-obsidian mb-3" data-testid="ai-gate-headline">
          {headline}
        </h1>
        <p className="text-sm text-muted-foreground mb-8 max-w-md mx-auto leading-relaxed">
          {description}
        </p>

        {/* Plan cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <div className="border border-violet-200 rounded-xl p-5 text-left hover:shadow-lg transition-shadow">
            <Sparkles size={18} className="text-violet-500 mb-2" />
            <h3 className="font-serif text-base text-obsidian">Creator AI</h3>
            <p className="text-2xl font-serif text-obsidian mt-1">€49<span className="text-xs text-muted-foreground font-sans">/month</span></p>
            <ul className="mt-3 space-y-1.5 text-[11px] text-obsidian/70">
              <li>3 AI images per day</li>
              <li>1 planner, 1 toolkit, 1 ebook/day</li>
              <li>AI wallpaper generation</li>
              <li>Marketplace + community access</li>
            </ul>
            <a href={`${CREATOR_LINK}?client_reference_id=${user?.id || ''}&prefilled_email=${encodeURIComponent(user?.email || '')}`} target="_blank" rel="noopener noreferrer">
              <Button className="w-full mt-4 rounded-lg h-9 text-[10px] uppercase tracking-wider bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white" data-testid="ai-gate-creator-btn">
                Subscribe <ArrowRight size={12} className="ml-1" />
              </Button>
            </a>
          </div>

          <div className="border border-rose-200 rounded-xl p-5 text-left hover:shadow-lg transition-shadow">
            <Zap size={18} className="text-rose-500 mb-2" />
            <h3 className="font-serif text-base text-obsidian">Full AI Studio</h3>
            <p className="text-2xl font-serif text-obsidian mt-1">€100<span className="text-xs text-muted-foreground font-sans">/month</span></p>
            <ul className="mt-3 space-y-1.5 text-[11px] text-obsidian/70">
              <li>10 AI images per day</li>
              <li>2 AI videos per day</li>
              <li>5 planners, toolkits, ebooks/day</li>
              <li>Full AI suite + priority processing</li>
            </ul>
            <a href={`${STUDIO_LINK}?client_reference_id=${user?.id || ''}&prefilled_email=${encodeURIComponent(user?.email || '')}`} target="_blank" rel="noopener noreferrer">
              <Button className="w-full mt-4 rounded-lg h-9 text-[10px] uppercase tracking-wider bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white" data-testid="ai-gate-studio-btn">
                Subscribe <ArrowRight size={12} className="ml-1" />
              </Button>
            </a>
          </div>
        </div>

        <div className="flex items-center justify-center gap-4">
          <Link to="/pricing">
            <Button variant="outline" className="rounded-lg h-10 text-xs uppercase tracking-wider" data-testid="ai-gate-pricing-link">
              Compare All Plans <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </Link>
        </div>

        <p className="text-[10px] text-muted-foreground mt-6">
          {isExpired ? 'Your previous plan benefits are paused until renewal.' : 'Your free trial still includes community & marketplace access.'}
        </p>
      </div>
    </div>
  );
}
